"use client";

import * as React from "react";
import { BrainCircuit, Compass, Workflow, Target, Sigma, ArrowRight, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
const philosophies = [{
  title: "Smart Sorting",
  description: "We meticulously categorize every load to identify items for reuse or recycling.",
  proof: "By separating materials at the source, we ensure that metals, plastics, and wood are diverted from Ottawa landfills and sent to specialized processing facilities.",
  icon: Workflow
}, {
  title: "Local Donation",
  description: "Gently used furniture and appliances are delivered to Ottawa charities.",
  proof: "We partner with local shelters and non-profits to give your unwanted items a second life, supporting families in Kanata, Nepean, and Orleans.",
  icon: Compass
}, {
  title: "Responsible Recycling",
  description: "Electronic waste and scrap metals are processed at certified eco-centers.",
  proof: "Our commitment to the environment means we follow strict provincial guidelines for hazardous materials, keeping toxic chemicals out of our local soil.",
  icon: BrainCircuit
}, {
  title: "Landfill Diversion",
  description: "Our goal is to keep 80% of all collected junk out of the waste stream.",
  proof: "Through aggressive sorting and donation programs, JUNKR has successfully diverted over 500 tons of debris from Ottawa's municipal landfills this year alone.",
  icon: Target
}];
export default function EducationalPhilosophy() {
  return <section data-section-id="236845" id="our-eco-friendly-mission" className="dark bg-background text-foreground py-24 px-6 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center justify-center p-2 bg-primary/10 rounded-lg mb-4">
            <Sigma className="size-5 text-primary" />
          </div>
          <h2 className="text-3xl md:text-5xl font-default font-bold tracking-tight text-foreground">
            Eco-Friendly Disposal for Ottawa Communities
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-default">
            At JUNKR, we are committed to responsible recycling and reuse across every Ottawa neighborhood. By partnering with 12+ local reuse facilities, we ensure that 85% of collected items are diverted from landfills, recycling over 500 tons of material annually to keep our community green.
          </p>
          <div className="pt-4">
            <Button asChild variant="secondary" size="lg" className="rounded-lg font-bold">
              <Link to="#">
                Partner with us
                <ArrowRight className="ml-2 size-4" />
              </Link>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {philosophies.map((item, i) => <div key={i} data-index={i} className="h-full">
              <HoverCard openDelay={200} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <div className="h-full cursor-crosshair outline-none">
                    <Card className="h-full bg-card/40 backdrop-blur-md border-border/50 hover:border-primary/50 hover:bg-card/60 transition-all duration-500 group shadow-lg hover:shadow-primary/10 rounded-lg">
                      <CardHeader className="pb-4">
                        <div className="size-12 rounded-lg bg-secondary/10 flex items-center justify-center mb-4 group-hover:bg-secondary/20 group-hover:scale-110 transition-all duration-500">
                          <item.icon className="size-6 text-primary" />
                        </div>
                        <CardTitle className="font-default text-xl tracking-tight text-foreground">
                          {item.title}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-muted-foreground/90 text-base leading-relaxed font-default">
                          {item.description}
                        </CardDescription>
                        <div className="mt-8 text-xs font-mono text-primary flex items-center gap-2 opacity-70 group-hover:opacity-100 transition-opacity duration-300">
                          <span>View Impact Data</span>
                          <ArrowRight className="size-3 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent side="bottom" align="start" className="w-80 bg-popover/95 backdrop-blur-xl border-border/60 shadow-2xl p-5 rounded-lg">
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold font-default flex items-center gap-2 text-foreground">
                      <span className="bg-primary/20 text-primary px-1.5 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider">
                        Impact
                      </span>
                      The Ottawa Result
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed font-default">
                      {item.proof}
                    </p>
                    <div className="pt-2 border-t border-border/50 flex justify-end">
                      <span className="text-[10px] font-mono text-primary italic">
                        ECO-CERTIFIED
                      </span>
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>)}
        </div>
      </div>
    </section>;
}